import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ResearchProgressStepper, Step } from './ResearchProgressStepper';
import { getProductBySlug } from '@/data/adminProducts';

interface ReEnrichmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  currentUrl?: string;
  onSuccess?: () => void;
}

export const ReEnrichmentModal = ({
  open,
  onOpenChange,
  productId,
  currentUrl,
  onSuccess,
}: ReEnrichmentModalProps) => {
  const [url, setUrl] = useState(currentUrl || '');
  const [category, setCategory] = useState('crm');
  const [preset, setPreset] = useState<'product' | 'service' | 'education'>('service');
  const [enrichmentType, setEnrichmentType] = useState<'quick' | 'deep'>('quick');
  const [isEnriching, setIsEnriching] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [result, setResult] = useState<any>(null);
  const [steps, setSteps] = useState<Step[]>([
    { id: 'scraping', label: 'Scraping website', status: 'waiting' },
    { id: 'extraction', label: 'Extracting capabilities, integrations & testimonials (Gemini)', status: 'waiting' },
    { id: 'generation', label: 'Generating product data (Gemini)', status: 'waiting' },
    { id: 'qa_generation', label: 'Generating FAQs (Gemini)', status: 'waiting' },
    { id: 'validation', label: 'Validating data', status: 'waiting' },
    { id: 'database', label: 'Updating database', status: 'waiting' },
  ]);
  const [detailedLog, setDetailedLog] = useState<Array<{
    step: string;
    substep: string | null;
    timestamp: Date;
  }>>([]);

  const handleEnrich = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    setIsEnriching(true);
    setProgress('Starting enrichment...');
    setResult(null);
    setDetailedLog([]);

    try {
      if (enrichmentType === 'deep') {
        // Reset steps
        setSteps([
          { id: 'scraping', label: 'Scraping website', status: 'waiting' },
          { id: 'extraction', label: 'Extracting capabilities, integrations & testimonials (Gemini)', status: 'waiting' },
          { id: 'generation', label: 'Generating product data (Gemini)', status: 'waiting' },
          { id: 'qa_generation', label: 'Generating FAQs (Gemini)', status: 'waiting' },
          { id: 'validation', label: 'Validating data', status: 'waiting' },
          { id: 'database', label: 'Updating database', status: 'waiting' },
        ]);

        // Fetch product by slug to get UUID
        const product = await getProductBySlug(productId);

        if (!product) {
          throw new Error('Product not found');
        }

        // Subscribe to realtime progress updates
        const channel = supabase
          .channel(`research-progress-${product.id}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'research_progress',
              filter: `product_id=eq.${product.id}`
            },
            (payload) => {
              const progress = payload.new as any;
              
              // Update main steps
              setSteps(prev => prev.map(s => {
                if (s.id === progress.step) {
                  return {
                    ...s,
                    status: progress.status === 'complete' ? 'complete' :
                           progress.status === 'error' ? 'error' : 'in-progress',
                    substep: progress.substep
                  };
                }
                return s;
              }));
              
              // Add to detailed log
              if (progress.substep) {
                setDetailedLog(prev => [...prev, {
                  step: progress.step,
                  substep: progress.substep,
                  timestamp: new Date()
                }]);
              }
            }
          )
          .subscribe();

        try {
          const { data, error } = await supabase.functions.invoke('deep-research-agent', {
            body: { productId: product.id, url, name: product.title || 'Product' }
          });

          if (error) throw error;

          // Update all steps as complete
          setSteps(prev => prev.map(s => ({ ...s, status: 'complete' as const })));

          toast.success('Deep research complete!');
          setResult(data);
          if (onSuccess) onSuccess();
        } finally {
          supabase.removeChannel(channel);
        }
      } else {
        setProgress('Scraping website...');
        const { data, error } = await supabase.functions.invoke('auto-draft-product', {
          body: { url, categorySlug: category, preset }
        });
        if (error) throw error;
        setProgress('Enrichment complete!');
        setResult(data);
        toast.success(data.mode === 'updated' ? 'Product re-enriched!' : 'Product enriched!');
      }
      if (onSuccess) setTimeout(() => { onSuccess(); handleClose(); }, 1500);
    } catch (error: any) {
      console.error('Enrichment error:', error);
      toast.error('Enrichment failed: ' + (error.message || 'Unknown error'));
      setProgress('');
    } finally {
      setIsEnriching(false);
    }
  };

  const handleClose = () => {
    if (!isEnriching) {
      setUrl(currentUrl || '');
      setProgress('');
      setResult(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Re-Run Product Enrichment</DialogTitle>
          <DialogDescription>
            Update this product by re-scraping its website and extracting fresh data with AI
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="url">Website URL</Label>
            <Input
              id="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isEnriching}
            />
          </div>

          <div className="space-y-2">
            <Label>Enrichment Mode</Label>
            <RadioGroup 
              value={enrichmentType} 
              onValueChange={(value: 'quick' | 'deep') => setEnrichmentType(value)}
              disabled={isEnriching}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="quick" id="quick" />
                <Label htmlFor="quick" className="font-normal cursor-pointer">
                  Quick Enrichment - Fast scraping and basic AI extraction
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deep" id="deep" />
                <Label htmlFor="deep" className="font-normal cursor-pointer">
                  Deep Research - Comprehensive AI analysis with capabilities and integrations
                </Label>
              </div>
            </RadioGroup>
          </div>

          {enrichmentType === 'quick' && (
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={setCategory} disabled={isEnriching}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crm">CRM</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="lead-gen">Lead Gen</SelectItem>
                  <SelectItem value="transaction">Transaction</SelectItem>
                  <SelectItem value="coaching">Coaching</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preset">Type</Label>
              <Select value={preset} onValueChange={(v: any) => setPreset(v)} disabled={isEnriching}>
                <SelectTrigger id="preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          )}

          {enrichmentType === 'deep' && steps.some(s => s.status !== 'waiting') && (
            <div className="py-4 border-t border-b">
              <ResearchProgressStepper steps={steps} />
            </div>
          )}

          {enrichmentType === 'quick' && progress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>{progress}</AlertDescription>
            </Alert>
           )}

          {result && result.timings && (
            <Alert>
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription>
                <div className="font-semibold mb-2">Research Complete</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total time: <span className="font-mono">{(result.timings.total_ms / 1000).toFixed(1)}s</span></div>
                  <div>Gemini calls: <span className="font-mono">
                    {((result.timings.gemini_extraction_ms + result.timings.gemini_generation_ms) / 1000).toFixed(1)}s
                  </span></div>
                  <div>Capabilities: <span className="font-semibold">{result.capabilitiesFound || 0}</span></div>
                  <div>Integrations: <span className="font-semibold">{result.integrationsFound || 0}</span></div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {result && !result.timings && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-semibold">
                  {result.mode === 'updated' ? 'Product Updated' : 'Product Created'}
                </span>
              </div>

              {result.warnings?.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-1">Warnings:</div>
                    <ul className="list-disc list-inside text-sm">
                      {result.warnings.map((w: string, i: number) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Confidence Score</div>
                  <div className="font-semibold">
                    {Math.round((result.provenance?.confidence || 0) * 100)}%
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Conflicts Detected</div>
                  <div className="font-semibold">{result.provenance?.conflicts || 0}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Pages Scraped</div>
                  <div className="font-semibold">
                    {result.stats?.scrapedPages || 0}
                    {result.stats?.failedPages > 0 && (
                      <span className="text-destructive ml-1">
                        ({result.stats.failedPages} failed)
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Plans Created</div>
                  <div className="font-semibold">{result.stats?.plansCreated || 0}</div>
                </div>
              </div>

              {result.stats?.failedUrls?.length > 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-1">Failed URLs:</div>
                    <ul className="text-xs space-y-1">
                      {result.stats.failedUrls.map((f: any, i: number) => (
                        <li key={i} className="truncate">
                          {f.url}: {f.error}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Badge variant={result.provenance?.conflicts > 0 ? 'destructive' : 'default'}>
                  {result.provenance?.conflicts > 0 ? 'Has Conflicts' : 'No Conflicts'}
                </Badge>
                <Badge variant="outline">
                  {result.provenance?.layers?.length || 0} Data Layers
                </Badge>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isEnriching}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={handleEnrich} disabled={isEnriching || !url}>
              {isEnriching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enriching...
                </>
              ) : (
                'Run Enrichment'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
